@import './pk-button.variables';

@layer components {
  // Common button base style
  .pk-button {
    @apply box-border flex h-[--button--height] w-full cursor-pointer select-none items-center justify-center shadow-none outline-none;
    @apply rounded-[--button--border-radius] border-[length:--button--border-width] border-solid border-[color:--button--border-color] bg-[color:--button--background-color];
    @apply text-center text-[length:--button--font-size] leading-[--button--font-lh] text-[color:--button--color];
    @apply px-[--button--horizontal-padding] py-[--button--vertical-padding];
    @apply transition duration-300 ease-in-out;
    // Focus state
    @apply [&:not(.disabled)]:focus-visible:focus-outline;
    // Disabled state
    @apply [&.disabled]:pointer-events-none [&.disabled]:opacity-40;

    font-family: var(--button--font-family);

    ::slotted(pk-icon) {
      @apply pointer-events-none [--pk-icon--color:--button--color];
    }
  }
}

/* -------------------------------------------------------------------------- */
/*                                    Host                                    */
/* -------------------------------------------------------------------------- */

:host {
  @apply inline-block align-middle outline-none sm:w-auto;
  -webkit-tap-highlight-color: theme('colors.transparent');
}

:host(.adaptive-width) {
  @apply w-full sm:w-auto sm:min-w-[--button--min-width];
}

:host(.fixed-width) {
  @apply w-[--button--width];
}

:host(.high-contrast) {
  .pk-button {
    --button--color: theme('colors.interactionHighContrast');
    --button--border-color: theme('colors.interactionHighContrast');
    --button--background-color: rgba(0, 0, 0, 0.2);

    @apply backdrop-blur;
    // Hover state
    @apply hover:[--button--background-color:rgba(0,0,0,0.3)];
    // Active state
    @apply active:[--button--background-color:rgba(0,0,0,0.4)];
    // Disabled state
    @apply disabled:opacity-40;
  }

  .pk-button--outlined {
    @apply [--button--background-color:rgba(0,_0,_0,_0.2)];
  }

  .pk-button--text {
    @apply border-none [--button--background-color:theme('colors.transparent')];
    @apply hover:[--button--background-color:theme('colors.transparent')] active:[--button--background-color:theme('colors.transparent')];
    // Focus state
    @apply [&:not(.disabled)]:focus-visible:[--button--background-color:rgba(255,_255,_255,_0.2)];
  }

  .pk-button--icon {
    @apply hover:border-transparent active:border-transparent;
  }
}

/* -------------------------------------------------------------------------- */
/*                            Appearance modifiers                            */
/* -------------------------------------------------------------------------- */

.pk-button--contained {
  @apply [--button--background-color:theme('colors.interaction')] [--button--border-color:theme('colors.interaction')] [--button--color:theme('colors.interactionCompl')];

  &:hover {
    --button--color: #{lighten(var(--pk-interaction-compl), 20%)};
    --button--border-color: #{lighten(var(--pk-interaction), 20%)};
    --button--background-color: #{lighten(var(--pk-interaction), 20%)};
  }
}

.pk-button--outlined {
  @apply hover:[--button--background-color:theme('colors.interaction')] hover:[--button--color:theme('colors.interactionCompl')];
}

.pk-button--icon {
  @apply h-auto rounded-full border-[length:--button--icon--border-width] p-[calc(4px-var(--button--icon--border-width))] outline-none [--button--background-color:--button--icon-background];

  &:hover {
    --button--color: #{lighten(var(--pk-interaction-compl), 20%)};
    --button--background-color: #{lighten(var(--pk-interaction), 20%)};
    --button--border-color: #{lighten(var(--pk-interaction), 20%)};
  }
}

.pk-button--icon.is-soft {
  --button--background-color: theme('colors.ui.01');
  --button--border-color: theme('colors.transparent');

  @apply hover:[--button--background-color:theme('colors.ui.02')];
  @apply active:[--button--background-color:theme('colors.ui.03')] active:[--button--border-color:theme('colors.transparent')];

  ::slotted(pk-icon) {
    --pk-icon--color: theme('colors.text.02');
  }
}

.pk-button--contained,
.pk-button--outlined,
.pk-button--icon {
  &:active {
    --button--color: #{darken(var(--pk-interaction-compl), 20%)};
    --button--border-color: #{darken(var(--pk-interaction), 20%)};
    --button--background-color: #{darken(var(--pk-interaction), 20%)};
  }
}

.pk-button--text {
  --button--background-color: theme('colors.transparent');

  @apply h-auto border-none p-0;
  // Focus state
  @apply [&:not(.disabled)]:focus-visible:[--button--background-color:theme('colors.ui.02')];

  &:hover {
    --button--color: #{lighten(var(--pk-interaction), 20%)};
    --button--background-color: theme('colors.transparent');
  }

  &:active {
    --button--color: #{darken(var(--pk-interaction), 20%)};
    --button--background-color: theme('colors.transparent');
  }
}
